import React, { useEffect, useState } from 'react';

import { postResources2 } from '@api/FW/balance/balance.api';
import { apiErrorHandler } from '@app/services/error.service';
import { ResourceCard } from '@components/shared/ResourceCard/ResourceCard';
import { IAccumulatedBalance, IUserBalance } from '@components/shared/ResourcesSummary/types';
import { getHiddenWallets } from '@FW/api/interfacePreferences/resourcesSummary';
import { useAppSelector } from '@hooks/shared/reduxHooks';
import { useResponsive } from '@hooks/shared/useResponsive';
import { Resources } from '@interfaces/FW/Resources';
import ResourcesSummaryConfigModal from '@shared/components/ResourcesSummary/components/ResourcesSummaryConfigModal';
import { Container } from '@styled/shared/Container';
import { Flex, Skeleton, Typography } from 'antd';
import { t } from 'i18next';

import EDiloBalanceOverview from '../../../cabinets/FW/features/eDilo/components/EDiloBalanceOverview';

const { Text } = Typography;

const ResourcesSummary = () => {
	const { transitWallets, noServiceWallets } = useAppSelector((state) => state.wallets);

	const { mobileOnly, tabletOnly } = useResponsive();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [hiddenWallets, setHiddenWallets] = useState<string[]>([]);
	const [balances, setBalances] = useState<{
		aggregated: IAccumulatedBalance[];
		walletBalances: IUserBalance[];
	}>();

	// Initial data fetch.
	useEffect(() => {
		if (!transitWallets || !noServiceWallets) return;

		fetchData();
	}, [transitWallets, noServiceWallets]);

	const fetchData = () => {
		setIsLoading(true);

		let excludedWalletsId: string[];

		getHiddenWallets()
			.then((loadData) => {
				excludedWalletsId = loadData.map((item: any) => item.Wallet);
			})
			.then(() => {
				const wallets = [...transitWallets, ...noServiceWallets]
					.filter(({ walletGUID }) => !excludedWalletsId.includes(walletGUID))
					.map(({ walletGUID }) => walletGUID);

				return postResources2('wallet', wallets);
			})
			.then((loadData) => {
				const aggregated = getNomenclaturesBalances(loadData);
				const walletBalances = getWalletsWithBalances(loadData);

				setBalances({ aggregated, walletBalances });
				setHiddenWallets(excludedWalletsId);
			})
			.catch((error) => apiErrorHandler(error))
			.finally(() => setIsLoading(false));
	};

	/**
	 * Iterates through user wallets and associates their balances with each wallet.
	 *
	 * @param data - Array with all the resources
	 */
	const getWalletsWithBalances = (data: Resources[]): IUserBalance[] =>
		Object.values(
			data.reduce((acc: any, item) => {
				const { walletGUID, walletName } = item;

				// Initialize entry for account if not already present
				if (!acc[walletGUID]) {
					acc[walletGUID] = {
						walletName: walletName,
						walletId: walletGUID,
						balances: [],
					};
				}

				// Collect balances for each account
				acc[walletGUID].balances.push({
					nomenclature: item.nomenclature,
					nomenclatureName: item.nomenclatureName,
					balance: item.balance,
				});

				return acc;
			}, {}),
		);

	/**
	 * Iterates through all resources to calculate the total balance for a specific resource type.
	 *
	 * @param data - Array with all the resources
	 */
	const getNomenclaturesBalances = (data: Resources[]): IAccumulatedBalance[] =>
		Object.values(
			data.reduce((acc: any, item) => {
				const { nomenclature, nomenclatureName, balance } = item;

				acc[nomenclature]
					? // Sum if already exists
						(acc[nomenclature].balance += balance)
					: // Create new entry otherwise
						(acc[nomenclature] = {
							nomenclature,
							nomenclatureName,
							balance,
						});

				return acc;
			}, {}),
		);

	return (
		<Container vertical gap={16} className='content-wrapper'>
			<Flex gap={16} justify='space-between' align='center'>
				<Text className='block-title'>{t('wallet.resourceSummary.title')}</Text>
				<ResourcesSummaryConfigModal finallyLogic={fetchData} hiddenWallets={hiddenWallets} />
			</Flex>

			<Flex
				gap={16}
				style={{
					display: 'grid',
					gridTemplateColumns: mobileOnly
						? 'repeat(2, 1fr)'
						: tabletOnly
							? 'repeat(3, 1fr)'
							: 'repeat(4, 1fr)',
				}}>
				{isLoading ? (
					[...Array(4)].map((_, index) => (
						<Skeleton.Button
							style={{ height: '168px' }}
							className={'block--i w-full--i'}
							key={index}
							active
						/>
					))
				) : (
					<>
						<ResourceCard
							isMain
							walletName={t('wallet.resourceSummary.summaryBalance')}
							nomenclatures={balances?.aggregated || []}
						/>
						{balances?.walletBalances
							?.slice(0, 3)
							?.map(({ walletName, walletId, balances }) => (
								<ResourceCard
									key={walletId}
									walletId={walletId}
									walletName={walletName}
									nomenclatures={balances}
								/>
							))}
					</>
				)}
				<EDiloBalanceOverview />
			</Flex>
		</Container>
	);
};

export default ResourcesSummary;
